import classNames from 'classnames';
import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { Analytics, getCurrentPlatformLink } from '@bpm-web-app/utils';
import { UserPlaylistWithUser, UserPlaylistCollaborationAccessLevel } from '@bpm-web-app/download-api-sdk';
import PlaylistIcon from '../../assets/icons/playlist-item-dynamic.svg';
import styles from './nav-my-playlist-list.module.css';
import categoryStyles from '../nav-my-playlist-categories/nav-my-playlist-categories.module.css';

// eslint-disable-next-line import/no-cycle
import { CustomIcon } from '../shared/custom-icon/custom-icon';
import Droppable, { ALL_TYPES_NO_ARTIST } from '../droppable/droppable';

export interface NavMyPlaylistListItemProps {
    isDragging?: boolean;
    containsActiveInList: boolean;
    item: UserPlaylistWithUser;
    hasIcon?: boolean;
}

export function NavMyPlaylistListItem({ item, hasIcon, containsActiveInList, isDragging = false }: NavMyPlaylistListItemProps) {
    const router = useRouter();

    const handleClick = (path: string) => {
        router.push(path);
    };

    // eslint-disable-next-line @typescript-eslint/dot-notation
    const isActive = useCallback((id: string) => router.query['playlistId'] && router.query['playlistId'] === id, [router]);

    return (
        <Droppable
            accept={ALL_TYPES_NO_ARTIST}
            onDrop={() => {
                return { target: 'playlist', id: item.id };
            }}
            disabled={item.access_level === UserPlaylistCollaborationAccessLevel.View}
        >
            {(state) => {
                const classes = hasIcon
                    ? classNames(styles['nav-my-playlist-list-item'], {
                        [styles['nav-my-playlist-list-item--active']]: isActive(item.id),
                    })
                    : classNames(styles['nav-my-playlist-list-item'], styles['nav-my-playlist-list-item--link'], styles['nav-my-playlist-list-item--no-overflow'], {
                        [styles['nav-my-playlist-list-item--is-dragging']]: isDragging,
                        [styles['nav-my-playlist-list-item--is-dragging--over']]: state.isOver,
                        [styles['nav-my-playlist-list-item--active']]: isActive(item.id),
                        [styles['nav-my-playlist-list-item--active-hover']]: containsActiveInList,
                    });

                return (
                    <button
                        type="button"
                        disabled={isDragging && item.access_level === UserPlaylistCollaborationAccessLevel.View}
                        aria-label="Go to playlist"
                        onClick={() => {
                            handleClick(getCurrentPlatformLink(`/my-playlist/${item.id}`));
                            Analytics.trackClick('my_playlist', item.id, { location: 'nav_my_playlist' });
                        }}
                        className={classes}
                    >
                        {hasIcon ? (
                            <>
                                <i className={categoryStyles['nav-my-playlists-category-item__button']}>
                                    <PlaylistIcon />
                                </i>
                                <span>{item.title}</span>
                            </>
                        ) : (
                            <span className={styles['nav-my-playlist-list-item__playlist-folder']}>{item.title}</span>
                        )}

                        {item.users && item.users.length > 0 ? <CustomIcon type="shared-with-me" isIconHoverActive={isActive(item.id) || false} tooltip="Collaborators Added" color="boulder" /> : null}
                    </button>
                );
            }}
        </Droppable>
    );
}

export default NavMyPlaylistListItem;
